import { useCallback, useEffect, useRef } from "react";
import { EditorContent, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  Bold,
  Code,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { uploadBlogImage } from "@/services/blogService";

export interface RichTextEditorValue {
  html: string;
  json: Record<string, unknown>;
}

interface RichTextEditorProps {
  value: RichTextEditorValue;
  onChange: (next: RichTextEditorValue) => void;
  placeholder?: string;
}

const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

async function uploadAndInsert(editor: Editor, file: File, t: (k: string) => string): Promise<void> {
  if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
    toast.error(t("blog.editor.toast.imageType"));
    return;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    toast.error(t("blog.editor.toast.imageTooLarge"));
    return;
  }
  const toastId = toast.loading(t("blog.editor.toast.uploadingImage"));
  try {
    const { url } = await uploadBlogImage(file);
    editor.chain().focus().setImage({ src: url, alt: file.name }).run();
    toast.success(t("blog.editor.toast.imageUploaded"), { id: toastId });
  } catch {
    toast.error(t("blog.editor.toast.imageUploadError"), { id: toastId });
  }
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
}: RichTextEditorProps): JSX.Element {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({
        placeholder: placeholder ?? t("blog.editor.placeholder"),
      }),
    ],
    content: value.json && Object.keys(value.json).length > 0 ? value.json : value.html,
    editorProps: {
      attributes: {
        class: cn(
          "prose prose-sm dark:prose-invert max-w-none min-h-[480px] focus:outline-none px-4 py-3",
          "prose-headings:scroll-mt-20 prose-img:rounded-lg",
        ),
      },
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items ?? []);
        const imageItem = items.find((i) => i.type.startsWith("image/"));
        if (imageItem) {
          const file = imageItem.getAsFile();
          if (file && editor) {
            event.preventDefault();
            void uploadAndInsert(editor, file, t);
            return true;
          }
        }
        return false;
      },
      handleDrop: (view, event) => {
        const files = Array.from(event.dataTransfer?.files ?? []);
        const imageFile = files.find((f) => f.type.startsWith("image/"));
        if (imageFile && editor) {
          event.preventDefault();
          void uploadAndInsert(editor, imageFile, t);
          return true;
        }
        return false;
      },
    },
    onUpdate({ editor: e }) {
      onChange({
        html: e.getHTML(),
        json: e.getJSON() as Record<string, unknown>,
      });
    },
  });

  // Keep editor in sync when value is replaced from outside (load existing
  // post, AI draft injection, etc.). The editor's own onUpdate emits the
  // current html back as value.html, so this short-circuits during typing.
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() === value.html) return;
    editor.commands.setContent(
      value.json && Object.keys(value.json).length > 0 ? value.json : value.html,
      { emitUpdate: false },
    );
  }, [editor, value]);

  const triggerImageUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file || !editor) return;
      await uploadAndInsert(editor, file, t);
    },
    [editor, t],
  );

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt(t("blog.editor.linkPrompt"), previousUrl ?? "");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor, t]);

  if (!editor) {
    return (
      <div className="min-h-[480px] rounded-lg border bg-card" />
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      <div className="flex flex-wrap items-center gap-1 border-b bg-muted/40 px-2 py-1.5">
        <ToolbarButton
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          label={t("blog.editor.toolbar.h2")}
          icon={Heading2}
        />
        <ToolbarButton
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          label={t("blog.editor.toolbar.h3")}
          icon={Heading3}
        />
        <ToolbarDivider />
        <ToolbarButton
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          label={t("blog.editor.toolbar.bold")}
          icon={Bold}
        />
        <ToolbarButton
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          label={t("blog.editor.toolbar.italic")}
          icon={Italic}
        />
        <ToolbarButton
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          label={t("blog.editor.toolbar.underline")}
          icon={UnderlineIcon}
        />
        <ToolbarButton
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          label={t("blog.editor.toolbar.strike")}
          icon={Strikethrough}
        />
        <ToolbarDivider />
        <ToolbarButton
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          label={t("blog.editor.toolbar.bulletList")}
          icon={List}
        />
        <ToolbarButton
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          label={t("blog.editor.toolbar.orderedList")}
          icon={ListOrdered}
        />
        <ToolbarButton
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          label={t("blog.editor.toolbar.quote")}
          icon={Quote}
        />
        <ToolbarButton
          active={editor.isActive("codeBlock")}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
          label={t("blog.editor.toolbar.code")}
          icon={Code}
        />
        <ToolbarDivider />
        <ToolbarButton
          active={editor.isActive("link")}
          onClick={setLink}
          label={t("blog.editor.toolbar.link")}
          icon={LinkIcon}
        />
        <ToolbarButton
          onClick={triggerImageUpload}
          label={t("blog.editor.toolbar.image")}
          icon={ImageIcon}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={handleFileChange}
        />
        <div className="ml-auto flex items-center gap-1">
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            label={t("blog.editor.toolbar.undo")}
            icon={Undo}
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            label={t("blog.editor.toolbar.redo")}
            icon={Redo}
          />
        </div>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

interface ToolbarButtonProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
}

function ToolbarButton({
  icon: Icon,
  label,
  onClick,
  active,
  disabled,
}: ToolbarButtonProps): JSX.Element {
  return (
    <Button
      type="button"
      variant={active ? "secondary" : "ghost"}
      size="icon"
      className="h-8 w-8"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      aria-pressed={active}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
}

function ToolbarDivider(): JSX.Element {
  return <span className="mx-1 h-5 w-px bg-border" />;
}
